import React, { useEffect, useState } from 'react';
import { Linking, Text, TextInput, View } from 'react-native';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  OperationalFilterChipGroup,
  OperationalFilterFieldLabel,
  OperationalFilterSectionDivider,
  OperationalFilterSegmentedRow,
} from '../../../shared/filters/OperationalFilterPrimitives';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import type { BranchSettingsScreenViewModel } from '../useBranchSettingsScreen';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import {
  fetchPrintAgentHealth,
  fetchPrintAgentPrinters,
  PRINT_AGENT_DOWNLOAD_URL,
  type AgentPrinter,
  type PrintAgentHealth,
} from '../../../printing/public-api';
import { SettingsSaveCta, hubStyles } from './settingsHubUi';

/**
 * Comanda cocina — solo para negocios con la capability `kitchen_tickets`.
 * Vive en Dispositivos porque decide a qué hardware sale el papel.
 * "Otra impresora" sale por el mismo agente de impresión
 * (cola de Windows o térmica de red tcp:IP:9100).
 */
export function ComandaCard({ vm }: { vm: BranchSettingsScreenViewModel }) {
  const hasKitchen = useHasBusinessCapability('kitchen_tickets');
  const [kitchenDraft, setKitchenDraft] = useState(vm.printConfig.kitchenPrinterName);
  const [agentPrinters, setAgentPrinters] = useState<AgentPrinter[]>([]);
  const [agentHealth, setAgentHealth] = useState<PrintAgentHealth | null>(null);
  const [agentProbing, setAgentProbing] = useState(false);
  const wantsOtherPrinter = vm.printConfig.kitchenPrinterName !== '';
  const comandaUsesAgent = vm.printConfig.mode === 'agent' || vm.printConfig.autoPrintComanda;

  useEffect(() => {
    setKitchenDraft(vm.printConfig.kitchenPrinterName);
  }, [vm.printConfig.kitchenPrinterName, vm.currentBranch?.id]);

  // Lista de colas de Windows solo cuando el usuario elige "Otra impresora".
  useEffect(() => {
    if (!hasKitchen || !wantsOtherPrinter || !comandaUsesAgent) return;
    let alive = true;
    void fetchPrintAgentPrinters(vm.printConfig).then((res) => {
      if (alive && res.ok) setAgentPrinters(res.printers);
    });
    return () => {
      alive = false;
    };
  }, [hasKitchen, wantsOtherPrinter, comandaUsesAgent, vm.printConfig]);

  const probeAgent = async () => {
    if (agentProbing) return;
    setAgentProbing(true);
    try {
      setAgentHealth(await fetchPrintAgentHealth(vm.printConfig));
    } finally {
      setAgentProbing(false);
    }
  };

  if (!hasKitchen) return null;

  const saveKitchenPrinter = () => {
    const name = kitchenDraft.trim();
    if (name !== vm.printConfig.kitchenPrinterName) {
      void vm.handleSavePrintConfig({ kitchenPrinterName: name });
    }
  };

  return (
    <PaperSheet padded testID="branch.comanda.config">
      <Text style={styles.cardTitle}>Comandas de cocina</Text>
      <Text style={styles.captionBlock}>El pedido que se imprime en cocina al cobrar.</Text>

      <View style={hubStyles.controlBlock}>
        <OperationalFilterFieldLabel>Cuándo imprimir</OperationalFilterFieldLabel>
        <OperationalFilterSegmentedRow
          ticketTone
          layout="hug"
          options={[
            { value: 'off', label: 'Manual', accessibilityLabel: 'Imprimir la comanda manualmente' },
            { value: 'on', label: 'Automática', accessibilityLabel: 'Imprimir la comanda automáticamente al cobrar' },
          ]}
          value={vm.printConfig.autoPrintComanda ? 'on' : 'off'}
          onChange={(v) => {
            if (!vm.canEditPosRules || vm.printConfigSaving) return;
            void vm.handleSavePrintConfig({ autoPrintComanda: v === 'on' });
          }}
        />
        <Text style={hubStyles.hintSecondary}>
          En Automática, la comanda se imprime al cobrar. En Manual, podés imprimirla desde Cocina o el aviso.
        </Text>
      </View>

      {vm.printConfig.autoPrintComanda && vm.printConfig.mode !== 'agent' ? (
        <View style={hubStyles.controlBlock}>
          <OperationalFilterFieldLabel>Programa de impresión</OperationalFilterFieldLabel>
          <Text style={hubStyles.hintSecondary}>
            Las comandas automáticas salen por el mismo agente que la impresión directa. Descargalo e instalalo en esta computadora.
          </Text>
          {agentHealth ? (
            <Text style={hubStyles.hintMuted}>
              {agentHealth.ok ? 'Agente conectado.' : 'No pudimos conectarnos con el agente.'}
            </Text>
          ) : null}
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            <SettingsSaveCta
              label="Descargar agente"
              layoutWide={false}
              variant="secondary"
              onPress={() => void Linking.openURL(PRINT_AGENT_DOWNLOAD_URL)}
              testID="branch.comanda.agent-download"
            />
            <SettingsSaveCta
              label={agentProbing ? 'Comprobando…' : 'Comprobar conexión'}
              layoutWide={false}
              variant="secondary"
              onPress={() => void probeAgent()}
              disabled={agentProbing}
              testID="branch.comanda.agent-probe"
            />
          </View>
        </View>
      ) : null}

      {comandaUsesAgent ? (
        <>
          <OperationalFilterSectionDivider />

          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Dónde imprimir</OperationalFilterFieldLabel>
            <OperationalFilterSegmentedRow
              ticketTone
              layout="hug"
              options={[
                {
                  value: 'same',
                  label: 'Misma impresora',
                  accessibilityLabel: 'Comanda por la misma impresora del ticket',
                },
                {
                  value: 'other',
                  label: 'Otra impresora',
                  accessibilityLabel: 'Comanda por otra impresora',
                },
              ]}
              value={wantsOtherPrinter ? 'other' : 'same'}
              onChange={(v) => {
                if (!vm.canEditPosRules || vm.printConfigSaving) return;
                if (v === 'same' && wantsOtherPrinter) {
                  setKitchenDraft('');
                  void vm.handleSavePrintConfig({ kitchenPrinterName: '' });
                }
              }}
            />
            <Text style={hubStyles.hintMuted}>
              La impresora de cocina usa el mismo programa de impresión. No hace falta instalar otro.
            </Text>
          </View>

          {wantsOtherPrinter ? (
            <View style={hubStyles.controlBlock}>
              {agentPrinters.length > 0 ? (
                <OperationalFilterChipGroup
                  ticketTone
                  layout="hug"
                  options={agentPrinters.map((p) => ({
                    value: p.name,
                    label: p.name,
                    accessibilityLabel: `Comanda por ${p.name}`,
                  }))}
                  value={vm.printConfig.kitchenPrinterName}
                  disabled={!vm.canEditPosRules}
                  onChange={(id) => {
                    setKitchenDraft(id);
                    void vm.handleSavePrintConfig({ kitchenPrinterName: id });
                  }}
                />
              ) : null}
              <Text style={hubStyles.hintSecondary}>Nombre o dirección de la impresora</Text>
              <TextInput
                style={hubStyles.inputTicket}
                value={kitchenDraft}
                onChangeText={setKitchenDraft}
                onBlur={saveKitchenPrinter}
                onEndEditing={saveKitchenPrinter}
                placeholder="Ej.: Cocina o tcp:192.168.1.51:9100"
                placeholderTextColor={ticketColors.ink3}
                editable={vm.canEditPosRules && !vm.printConfigSaving}
                autoCapitalize="none"
                testID="branch.comanda.printer-name"
              />
            </View>
          ) : null}
        </>
      ) : null}
    </PaperSheet>
  );
}
